// src/api/invoices.ts
import { apiRequest } from "./http";

export type ApiOk<T> = { ok: true; data: T };
export type ApiErr = { ok: false; error: string };

export type InvoiceType = "detailed" | "change_order";
export type InvoiceStatus = "draft" | "final";

// DB row shape returned by functions/api/invoices/*
export type ApiInvoice = {
  id: string;
  tenant_id: string;
  project_id: string | null;

  invoice_number: string;
  invoice_type: InvoiceType;
  title: string;

  invoice_date: string; // YYYY-MM-DD
  due_date: string | null;

  bill_to_name: string;
  bill_to_address: string;

  project_label: string;

  scope_text: string;
  notes_text: string;

  tax_enabled: number; // 0/1
  tax_rate: number;

  status: InvoiceStatus;

  items_json: string; // JSON string array
  adjustments_json: string; // JSON string array
  applied_payment_ids_json: string; // JSON string array

  created_at: string;
  updated_at: string;
};

// Lightweight UI model (v1)
export type UiInvoice = {
  id: string;
  projectId: string | null;

  invoiceNumber: string;
  invoiceType: InvoiceType;
  title: string;

  invoiceDate: string;
  dueDate: string | null;

  billToName: string;
  billToAddress: string;

  projectLabel: string;

  scopeText: string;
  notesText: string;

  taxEnabled: boolean;
  taxRate: number;

  status: InvoiceStatus;

  itemsJson: string;
  adjustmentsJson: string;
  appliedPaymentIdsJson: string;
};

export function apiInvoiceToUi(x: ApiInvoice): UiInvoice {
  return {
    id: x.id,
    projectId: x.project_id ?? null,

    invoiceNumber: x.invoice_number,
    invoiceType: x.invoice_type,
    title: x.title,

    invoiceDate: x.invoice_date,
    dueDate: x.due_date ?? null,

    billToName: x.bill_to_name,
    billToAddress: x.bill_to_address,

    projectLabel: x.project_label,

    scopeText: x.scope_text ?? "",
    notesText: x.notes_text ?? "",

    taxEnabled: Boolean(x.tax_enabled),
    taxRate: Number(x.tax_rate) || 0,

    status: x.status,

    itemsJson: x.items_json ?? "[]",
    adjustmentsJson: x.adjustments_json ?? "[]",
    appliedPaymentIdsJson: x.applied_payment_ids_json ?? "[]",
  };
}

export function uiInvoiceToApi(x: Partial<UiInvoice>) {
  // Keep v1 simple: send only fields you provide.
  // Backend PATCH updates only provided fields.
  return {
    id: x.id,
    project_id: x.projectId ?? undefined,

    invoice_number: x.invoiceNumber ?? undefined,
    invoice_type: x.invoiceType ?? undefined,
    title: x.title ?? undefined,

    invoice_date: x.invoiceDate ?? undefined,
    due_date: x.dueDate ?? undefined,

    bill_to_name: x.billToName ?? undefined,
    bill_to_address: x.billToAddress ?? undefined,

    project_label: x.projectLabel ?? undefined,

    scope_text: x.scopeText ?? undefined,
    notes_text: x.notesText ?? undefined,

    tax_enabled: x.taxEnabled === undefined ? undefined : x.taxEnabled ? 1 : 0,
    tax_rate: x.taxRate ?? undefined,

    status: x.status ?? undefined,

    items_json: x.itemsJson ?? undefined,
    adjustments_json: x.adjustmentsJson ?? undefined,
    applied_payment_ids_json: x.appliedPaymentIdsJson ?? undefined,
  };
}

// --- API calls ---

export async function listInvoices(
  tenantId: string,
  opts?: { projectId?: string; status?: InvoiceStatus }
) {
  const qs = new URLSearchParams();
  if (opts?.projectId) qs.set("projectId", opts.projectId);
  if (opts?.status) qs.set("status", opts.status);

  const url = qs.toString() ? `/api/invoices?${qs.toString()}` : "/api/invoices";
  return apiRequest<ApiOk<ApiInvoice[]> | ApiErr>("GET", url, { tenantId });
}

export async function createInvoice(tenantId: string, body: any) {
  return apiRequest<ApiOk<ApiInvoice> | ApiErr>("POST", "/api/invoices", { tenantId, body });
}

export async function getInvoiceById(tenantId: string, id: string) {
  return apiRequest<ApiOk<ApiInvoice> | ApiErr>("GET", `/api/invoices/${id}`, { tenantId });
}

export async function updateInvoiceById(tenantId: string, id: string, body: any) {
  return apiRequest<ApiOk<ApiInvoice> | ApiErr>("PATCH", `/api/invoices/${id}`, { tenantId, body });
}

export async function deleteInvoiceById(tenantId: string, id: string) {
  return apiRequest<ApiOk<{}> | ApiErr>("DELETE", `/api/invoices/${id}`, { tenantId });
}
